import {
  AuthenticationPlusStatus,
  BrandAsset,
  BrandAssetAppeal,
  BrandAssetExtended,
  BrandDetail,
  BrandFeedback,
  ExtVettingRecord,
  Mno,
  MnoSuspensionStatus,
  RegisteredBrandAsset,
  ValidationRule,
  VerticalType,
  VettingAppeal,
  VettingProvider,
} from './types';
import { getAttachment } from '../Campaigns/apis';
import {
  globalDeleteService,
  globalGetService,
  globalPostService,
  globalPutService,
} from '../../../utils/globalApiServices';
import { getTimezone } from '../../../utils/time';
import { Pagination, SelectItem } from '../../../utils/types';

const compareMnos = (a: Mno, b: Mno) => a.osrBitmaskIndex - b.osrBitmaskIndex;

const compareSelectItems = (a: SelectItem, b: SelectItem) =>
  a.label.localeCompare(b.label);

export const getVettingRecords = async (brandUid: string) => {
  const response = await globalGetService(`csp/brands/${brandUid}/vettings`, {
    vettingStatus: 'ACTIVE,PENDING,UNSCORE,FAILED,EXPIRED',
  });
  if (response.ok) {
    return response.data as ExtVettingRecord[];
  }
  return [];
};

export const resend2faEmail = async (brandUid: string) => {
  const response = await globalPostService(`csp/brands/${brandUid}/2faEmail`);
  if (response.ok) {
    return brandUid;
  }
};

export const getBrands = async (query = {}) => {
  const response = await globalGetService('csp/brands', query);
  return response?.ok ? (response.data as Pagination<BrandDetail>) : undefined;
};

export const downloadBrandsAsCsv = async (
  query = {}
): Promise<ArrayBuffer | undefined> => {
  const response = await globalGetService(
    'csp/brands/csv',
    {
      ...query,
      timezone: getTimezone(),
    },
    'arraybuffer'
  );

  return response?.ok ? (response.data as ArrayBuffer) : undefined;
};

export const getBrandIdentityStatus = async () => {
  const response = await globalGetService('enums/brandIdentityStatus');
  return response.ok ? (response.data as string[]) : [];
};

export const getAttachmentAsImageFile = async (data: BrandAssetExtended) => {
  const attachment = await getAttachment(data.attachmentUuid);
  if (attachment) {
    return new File([attachment], data.name, {
      type: 'image/jpeg',
    });
  }
  return undefined;
};

export const registerBrandAsset = async (
  brandId: string,
  payload: {
    brandAssetType: string;
    name: string;
    attachmentUuid: string;
  }
): Promise<RegisteredBrandAsset | undefined> => {
  const response = await globalPostService(
    `csp/brands/${brandId}/asset`,
    payload
  );
  return response?.ok ? response.data : undefined;
};

export const deleteBrandAsset = async (
  brandId: string,
  name: string
): Promise<boolean> => {
  const response = await globalDeleteService(
    `csp/brands/${brandId}/asset/${name}`
  );
  return !!response?.ok;
};

export const getBrandAssets = async (
  brandId: string,
  query: Record<string, any> = {}
): Promise<BrandAsset[]> => {
  const response = await globalGetService(`csp/brands/${brandId}/asset`, query);
  return response?.ok ? (response.data as BrandAsset[]) : [];
};

export const getBrandAssetAppeals = async (
  brandId: string,
  name: string,
  query = {}
): Promise<Pagination<BrandAssetAppeal> | undefined> => {
  // TODO: Replace it when the API is ready
  // const response = await globalGetService(
  //   `csp/brands/${brandId}/asset/${name}/appeals`,
  //   query
  // );
  // return response?.ok
  //   ? (response.data as Pagination<BrandAssetAppeal>)
  //   : undefined;

  return new Promise((resolve) => {
    const responseData: Pagination<BrandAssetAppeal> = {
      totalRecords: 0,
      records: [],
      page: 1,
      recordsPerPage: 10,
    };
    setTimeout(() => {
      console.log('GET', `csp/brands/${brandId}/asset/${name}/appeals`, query);
      resolve(responseData);
    }, 500);
  });
};

export const getBrand = async (brandUid: string) => {
  const response = await globalGetService(`csp/brands/${brandUid}`);
  return response?.ok ? (response.data as BrandDetail) : undefined;
};

export const updateBrand = async (
  brandUid: string,
  data: Record<string, any> = {}
) => {
  const response = await globalPutService(`csp/brands/${brandUid}`, data);
  return response?.ok ? (response.data as BrandDetail) : undefined;
};

export const revetBrand = async (brandUid: string) => {
  const response = await globalPutService(
    `csp/brands/${brandUid}/vet?nonBlocking=true`
  );
  if (response?.ok) {
    return response.data as BrandDetail;
  }
  if (response?.data?.error) {
    const key = Object.keys(response.data.error)[0];
    const message = response.data.error[key][0];
    throw new Error(message);
  }
  return undefined;
};

export const getVettingAppeals = async (brandUid: string) => {
  const response = await globalGetService(
    `csp/vetting/appeal?brandUid=${encodeURIComponent(brandUid)}`
  );
  return (response?.ok ? response.data.records : []) as VettingAppeal[];
};

export const getBrandFeedback = async (brandUid: string) => {
  const response = await globalGetService(
    `csp/brands/${brandUid}/feedback`,
    undefined,
    {
      silent: true,
    }
  );
  return (response?.ok ? response.data.category : []) as BrandFeedback[];
};

export const getMnos = async () => {
  const response = await globalGetService('enums/mno');
  const mnos = (response?.ok ? response.data : []) as Mno[];
  mnos.sort(compareMnos);
  return mnos;
};

export const getMnoSuspensionStatus = async (brandUid: string) => {
  const response = await globalGetService(
    `csp/brands/${brandUid}/mnoSuspensionStatus`
  );
  return (response?.ok ? response.data : []) as MnoSuspensionStatus[];
};

export const getCountries = async () => {
  const response = await globalGetService('enums/countries');
  const countries = (response?.ok ? response.data : {}) as Record<
    string,
    string
  >;
  return Object.entries(countries)
    .map(([key, value]) => ({ label: value, value: key } as SelectItem))
    .sort(compareSelectItems);
};

export const getUsStates = async () => {
  const response = await globalGetService('enums/usStates');
  const states = (response?.ok ? response.data : {}) as Record<string, string>;
  return Object.entries(states)
    .map(([key, value]) => ({ label: value, value: key } as SelectItem))
    .sort(compareSelectItems);
};

export const getVerticalTypes = async () => {
  const response = await globalGetService('enums/verticalTypes');
  const types = (response?.ok ? response.data : []) as VerticalType[];
  return types.map(
    (type) => ({ label: type.displayName, value: type.id } as SelectItem)
  );
};

export const getStockExchanges = async () => {
  const response = await globalGetService('enums/stockExchanges');
  const exchanges = (response?.ok ? response.data : []) as string[];
  return exchanges.map(
    (exchange) => ({ label: exchange, value: exchange } as SelectItem)
  );
};

export const getVettingProviders = async () => {
  const response = await globalGetService('csp/evp');
  return (response?.ok ? response.data : []) as VettingProvider[];
};

export const getVettingProvidersByVettingClass = async () => {
  const response = await globalGetService('csp/evp/byVettingClass');
  return (response?.ok ? response.data : {}) as Record<
    string,
    VettingProvider[]
  >;
};

export const applyForVetting = async (
  brandUid: string,
  evpUid: string,
  data: Record<string, any> = {}
) => {
  const response = await globalPostService(
    `csp/brands/${brandUid}/vettings/${evpUid}`,
    data
  );
  if (response?.ok) {
    return response.data as ExtVettingRecord;
  }
  if (response?.data?.error) {
    const key = Object.keys(response.data.error)[0];
    const message = response.data.error[key][0];
    throw new Error(message);
  }
  return undefined;
};

export const importVettingRecords = async (
  brandUid: string,
  evpUid: string,
  vettingId: string,
  data: Record<string, any> = {}
) => {
  const response = await globalGetService(
    `csp/brands/${brandUid}/vettings/${evpUid}/import/${encodeURIComponent(
      vettingId
    )}`,
    data
  );
  if (response?.ok) {
    return response.data as ExtVettingRecord;
  }
  if (response?.data?.error) {
    const key = Object.keys(response.data.error)[0];
    const message = response.data.error[key][0];
    throw new Error(message);
  }
  return undefined;
};

export const resend2FA = async (
  brandUid: string,
  data?: { vettingClass: string }
) => {
  const response = await globalPostService(
    `csp/brands/${brandUid}/2faEmail`,
    data
  );
  return !!response?.ok;
};

export const requestRcsAppeal = async (
  brandUid: string,
  evpUid: string,
  vettingId: string,
  categories: string[],
  explanation: string,
  attachments: string[]
) => {
  const response = await globalPostService(
    `csp/vetting/appeal/brand/${brandUid}/rcs`,
    {
      evpUid,
      vettingId,
      appealCategories: categories,
      explanation,
      attachmentUuids: attachments,
    }
  );
  return response?.ok ? response.data : undefined;
};

export const requestAuthplusAppeal = async (
  brandUid: string,
  evpUid: string,
  vettingId: string,
  categories: string[],
  explanation: string,
  attachments: string[]
) => {
  const response = await globalPostService(
    `csp/vetting/appeal/brand/${brandUid}/authplus`,
    {
      evpUid,
      vettingId,
      appealCategories: categories,
      explanation,
      attachmentUuids: attachments,
    }
  );
  return response?.ok ? response.data : undefined;
};

export const getValidationRules = async () => {
  const response = await globalGetService('csp/validationRule', {
    entityType: 'BRAND',
  });
  return (response?.ok ? response.data.records : []) as ValidationRule[];
};

export const createBrand = async (data: Record<string, any>) => {
  const response = await globalPostService('csp/brands', data);
  return response?.ok ? (response.data as BrandDetail) : undefined;
};

export const getAuthenticationPlusStatus = async () => {
  const response = await globalGetService('enums/authenticationPlusStatus');
  return (response?.ok ? response.data : []) as AuthenticationPlusStatus[];
};
